import * as React from 'react';
import { format } from 'date-fns';
import { camelCase } from 'lodash';
import { useTranslation } from 'react-i18next';
import { faDatabase, faLink } from '@fortawesome/pro-regular-svg-icons';
import { withIcon } from '../../../core/components/Icon';
import Field from '../../../core/components/Fields/Field';
import Tooltip from '../../../core/components/Tooltip';
import classnames from '../../../core/utils/classnames';

const LinkIcon = withIcon(faLink);
const DatabaseIcon = withIcon(faDatabase);

export type MatchedLineItem = {
    pos?: string | null;
    articleNumber?: string | null;
    articleNumber2?: string | null;
    articleNumber3?: string | null;
    articleDescription?: string | null;
    quantity?: string | null;
    unit?: string | null;
    unitPrice?: string | null;
    priceUnit?: string | null;
    totalPrice?: string | null;
    articleDeliveryDate?: string | null;
};

type LineItemField = { fieldName: string; path: string };

const PRICE_KEYS = new Set(['unitPrice', 'totalPrice']);
const NUMBER_KEYS = new Set(['quantity', ...PRICE_KEYS]);
const DATE_KEYS = new Set(['articleDeliveryDate']);
const MISMATCH_RESULT = 'values_not_match';

const formatMatched = (fieldName: string, matched: MatchedLineItem): string | null => {
    const key = camelCase(fieldName);
    const raw = (matched as Record<string, string | null | undefined>)[key];
    if (raw == null || raw === '') return null;

    if (DATE_KEYS.has(key)) {
        const parsed = new Date(raw);
        return Number.isNaN(parsed.getTime()) ? raw : format(parsed, 'P');
    }

    if (NUMBER_KEYS.has(key)) {
        const n = Number(raw);
        if (Number.isNaN(n)) return raw;
        const lang = localStorage.getItem('i18nextLng') || 'en';
        return PRICE_KEYS.has(key)
            ? n.toLocaleString(lang, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            : n.toLocaleString(lang);
    }

    return String(raw);
};

const isFieldMismatch = (item: any, fieldName: string): boolean =>
    item?.[camelCase(fieldName)]?.confidenceExplanation?.explanationDetails?.document_matching_result ===
    MISMATCH_RESULT;

const MatchedValueCell = ({ item, fieldName }: { item: any; fieldName: string }) => {
    const matched = item?.matchedLineItem ? formatMatched(fieldName, item.matchedLineItem) : null;
    const hasMatch = Boolean(matched);
    const isMismatch = hasMatch && isFieldMismatch(item, fieldName);

    const valueTone = isMismatch && 'text-content-danger-strong font-medium';

    const fieldTone = isMismatch ? '!border-line-danger-strong !bg-surface-danger-weak' : '!bg-gray-75';

    const iconTone = isMismatch ? 'text-content-danger-strong' : 'text-content-weak';

    return (
        <Field readOnly className={classnames('w-full', fieldTone)}>
            <Field.Input>
                <div className={classnames('px-2 py-1.5 flex-1 min-w-0 truncate', valueTone)}>{matched ?? '—'}</div>
            </Field.Input>
            <Field.Controls>
                <div className={classnames('flex justify-center items-center min-w-7 min-h-7 text-sm', iconTone)}>
                    <DatabaseIcon />
                </div>
            </Field.Controls>
        </Field>
    );
};

type IndicatorProps = {
    isActive?: boolean;
    isPinned?: boolean;
    sourceDocumentNumber?: string | null;
    onHoverChange?: (isHovered: boolean) => void;
    onClick?: () => void;
};

const LineItemMatchIndicator = ({
    isActive,
    isPinned,
    sourceDocumentNumber,
    onHoverChange,
    onClick,
}: IndicatorProps) => {
    const { t } = useTranslation('assistance');
    const tooltip = sourceDocumentNumber
        ? t('itemsView.matchedLineItem.tooltip', { orderNumber: sourceDocumentNumber })
        : '';
    return (
        <Tooltip content={tooltip} side="top">
            <button
                type="button"
                className={classnames(
                    'ml-2 inline-flex align-middle items-center justify-center w-8 h-8 rounded transition-colors cursor-pointer',
                    isPinned
                        ? '!bg-surface-brand-medium-default hover:!bg-surface-brand-medium-hover'
                        : 'hover:bg-surface-strong'
                )}
                onMouseEnter={() => onHoverChange?.(true)}
                onMouseLeave={() => onHoverChange?.(false)}
                onClick={onClick}
                aria-pressed={isPinned}
            >
                <LinkIcon
                    className={classnames(
                        'text-content-success-strong transition-opacity',
                        isActive ? 'opacity-100' : 'opacity-70'
                    )}
                />
            </button>
        </Tooltip>
    );
};

export const InlineMatchedValue = ({
    item,
    fieldName,
    isVisible,
}: {
    item: any;
    fieldName: string;
    isVisible: boolean;
}) => (
    <div
        className={classnames(
            'overflow-hidden flex items-start transition-all duration-300 ease-out',
            isVisible ? 'flex-1 opacity-100 pl-3' : 'flex-[0_0_0px] opacity-0'
        )}
        aria-hidden={!isVisible}
    >
        <MatchedValueCell item={item} fieldName={fieldName} />
    </div>
);

export const MatchedFieldsRow = ({
    item,
    fields,
    columnWidths,
    controlsWidth,
    isVisible,
}: {
    item: any;
    fields: LineItemField[];
    columnWidths: number[] | null;
    controlsWidth: number | null;
    isVisible: boolean;
}) => {
    const { t } = useTranslation('assistance');
    return (
        <div
            className={classnames(
                'flex gap-0.5 overflow-hidden transition-all duration-300 ease-out',
                isVisible ? 'max-h-12 opacity-100 mt-2' : 'max-h-0 opacity-0'
            )}
            aria-hidden={!isVisible}
        >
            <div
                className="sticky left-0 z-10 bg-surface-weak flex-none p-px pr-2 flex items-center"
                style={{ width: controlsWidth ?? '6.5rem' }}
            >
                <span className="text-sm font-medium text-content-strong">
                    {t('itemsView.matchedLineItem.fromOrder')}
                </span>
            </div>
            <div className="flex gap-2">
                {fields.map((field, idx) => (
                    <div key={field.path} style={{ width: columnWidths?.[idx] }} className="flex-none min-w-0">
                        <MatchedValueCell item={item} fieldName={field.fieldName} />
                    </div>
                ))}
            </div>
        </div>
    );
};

export default LineItemMatchIndicator;
